import { useState } from 'react';
import { useCloseNotificationDetail, useCloseNotificationsPanel, useNotificationDetail } from '@/store/notificationsStore';
import { useCtaRouter } from '@/lib/notifications/useCtaRouter';
import { resolvePopupViewKey } from '@/lib/notifications/popup-view';
import { PopupShell } from './popups/PopupShell';
import { POPUP_VIEWS } from './popups/popup-registry';

/**
 * Per-type dispatcher for the notification detail pop-up. Reads the selected
 * notification from the store, resolves it to a view via
 * {@link resolvePopupViewKey} (type → view, with MARKET_RESOLUTION fanning out
 * by `meta.resolutionOutcome` and ORDER_FILLED by fill state), and renders that
 * view inside the shared responsive {@link PopupShell} (mobile bottom-sheet /
 * desktop dialog). Every PRD type has its own Figma view; the rest fall through
 * to the generic template.
 *
 * CTA actions are handled by {@link useCtaRouter}; the generic view also gets
 * `onClose` / `onNavigate` for its bespoke market deep-link + signup-reminder
 * flows. Mounted once at the app root.
 */
export function NotificationDetail() {
  const notification = useNotificationDetail();
  const closeDetail = useCloseNotificationDetail();
  const closePanel = useCloseNotificationsPanel();
  const runAction = useCtaRouter();

  // Navigating away tears down the whole surface: the panel stays open UNDER the
  // pop-up (popover on desktop, sheet on mobile) and would otherwise cover the
  // destination.
  const handleNavigate = () => {
    closeDetail();
    closePanel();
  };

  // Keep rendering the last notification through the sheet's 250ms exit slide so
  // the content doesn't blank mid-animation (render-phase state adjustment, same
  // pattern as BottomSheet's shouldRender).
  const [lastNotification, setLastNotification] = useState(notification);
  if (notification && notification !== lastNotification) {
    setLastNotification(notification);
  }
  const active = notification ?? lastNotification;

  const View = active ? POPUP_VIEWS[resolvePopupViewKey(active)] : undefined;

  return (
    <PopupShell open={notification != null} onClose={closeDetail}>
      {active && View && (
        <View
          key={active.id}
          notification={active}
          onAction={(action) => runAction(action, active)}
          onClose={closeDetail}
          onNavigate={handleNavigate}
        />
      )}
    </PopupShell>
  );
}
